import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  mapCategoryError,
  mapProductError,
  useAuthSession,
  useStoreCategories,
  useTranslation,
} from '@dukanti/shared';
import type { ProductErrorKey } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useCreateProduct } from '@/hooks/use-create-product';
import { ProductForm } from '@/features/products/components/product-form';
import type { ProductFormSubmitPayload } from '@/features/products/components/product-form';

/**
 * Route for creating a new product (F-003 AC1/AC2/AC10).
 *
 * Thin route shell: owns the `useCreateProduct` mutation, pulls
 * `storeId` from the auth / store context (both are guaranteed by
 * the `AuthGuard mode="require-store"` wrapper in `routes/index.tsx`),
 * and hands off everything else to `<ProductForm mode="create" />`.
 *
 * On success:
 *   - if *some* images failed to upload, we navigate to `/products`
 *     with `location.state = { failedFileNames }` so the list page
 *     shows a partial-failure banner (§18 item 1 / D3).
 *   - otherwise, navigate plain to `/products`.
 *
 * On failure:
 *   - the error is mapped via `mapProductError` to a translation key
 *     and shown as a banner above the action bar (not a toast —
 *     design mockups use an in-card banner).
 */
export function ProductNewRoute() {
  const { dir } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const categories = useStoreCategories(supabase, storeId);
  const createProduct = useCreateProduct();
  const [submitError, setSubmitError] = useState<ProductErrorKey | null>(null);

  const handleSubmit = async (payload: ProductFormSubmitPayload) => {
    setSubmitError(null);

    // AuthGuard already enforces a store, but guard defensively so
    // typing is clean and we never hit the mutation with a null.
    if (!storeId) {
      setSubmitError('common.errors.permissionDenied');
      return;
    }

    try {
      const result = await createProduct.mutateAsync({
        input: {
          store_id: storeId,
          ...payload.input,
        },
        mode: payload.mode,
        options: payload.options,
        variants: payload.variants,
        files: payload.newFiles,
      });

      navigate('/products', {
        replace: true,
        state: result.imagesFailed ? { failedFileNames: result.failedFileNames } : undefined,
      });
    } catch (err) {
      console.error('[product-new-route]', err);
      setSubmitError(mapProductError(err));
    }
  };

  const handleCancel = () => {
    navigate('/products');
  };

  const routeState = (location.state ?? {}) as {
    readonly prefillBarcode?: string;
    readonly deletedReuseConfirmedNormalized?: string | null;
  };
  const prefillBarcode = routeState.prefillBarcode ?? searchParams.get('barcode') ?? '';

  return (
    <main dir={dir} data-testid="product-new-route" className="bg-background min-h-screen">
      <ProductForm
        mode="create"
        categories={categories.categories}
        isLoadingCategories={categories.isLoading}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitError={
          submitError ?? (categories.isError ? mapCategoryError(categories.error) : null)
        }
        isSubmitting={createProduct.isPending}
        initialBarcode={prefillBarcode}
        initialValues={
          prefillBarcode
            ? {
                name: '',
                description: '',
                priceRaw: '',
                saleEnabled: false,
                salePriceRaw: '',
                inventory: 0,
                categoryId: '',
                images: [],
                mode: 'simple',
                prefillBarcode,
                deletedReuseConfirmedNormalized: routeState.deletedReuseConfirmedNormalized ?? null,
              }
            : undefined
        }
      />
    </main>
  );
}
